
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import React from "react";
import {
  getPathsFromContext,
  getResourceByPath,
  getResourceCollection,
  translatePathFromContext,
} from "next-drupal";
import Layout from "components/Layout";
import { DrupalJsonApiParams } from "drupal-jsonapi-params";
import { getGlobals } from "lib/getGlobals";
import PageHeader from "components/PageHeader";
import Meta from "components/Meta";
import filterTaxonomyPathsSlug from "lib/filterTaxonomyPathsSlug";
import TermList from "components/Terms/TermList";
import NodePageTeaser from "components/Page/NodePageTeaser";
import NodePageContent from "components/Page/NodePageContent";
import getParams from "lib/getParams";
import useTranslation from "next-translate/useTranslation";
import getFullResourceCollection from "lib/getFullResourceCollection";
import getFamilyTree from "lib/getFamilyTree";
import Body from "components/Body";

export default function TermNodeDefinitionsSlugPage({
  globals,
  resource,
  parents = [],
  nodes = [],
  childs = [],
}) {
  const { t } = useTranslation("common");
  if (!resource) return null;

  const title = resource.type === "node--page" ? resource.title : resource.name;
  const body =
    resource.type === "node--page" ? resource.body : resource.description;

  const crumbs = [
    {
      title: "definitions.title",
      path: "definitions.path",
      translate: true,
    },
  ];

  parents.map((parent) => {
    crumbs.push({
      title: parent.name,
      path: parent.path.alias,
      translate: false,
    });
  });

  return (
    <Layout globals={globals}>
      <Meta
        title={resource.field_meta_title || title}
        description={resource.field_meta_description || body?.summary}
        useTemplate
      />

      <PageHeader
        crumbs={[...crumbs, { title }]}
        title={title}
        image={resource?.field_image}
      />

      {resource.type === "node--page" && (
        <section className="py-10 lg:py-16" aria-label={title}>
          <div className="relative container mx-auto px-4">
            <NodePageContent node={resource} />
          </div>
        </section>
      )}
      {resource.type === "taxonomy_term--definitions" && (
        <>
          {(body || nodes.length > 0) && (
            <section className="py-10 lg:py-16" aria-label={title}>
              <div className="relative container mx-auto px-4">
                {body && (
                  <div className="mx-auto lg:max-w-screen-lg mb-10 lg:pb-16">
                    <div className="prose max-w-none mx-0">
                      <Body value={body.processed} />
                    </div>
                  </div>
                )}
                {nodes.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                    {nodes?.map((node) => (
                      <div key={node.id}>
                        <NodePageTeaser node={node} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}
          {childs.length > 0 && (
            <section
              className="relative pt-16 pb-24 bg-secondary-light"
              aria-labelledby="#definition-categories-title"
            >
              <div className="container mx-auto px-4 transition-all">
                <h2
                  id="definition-categories-title"
                  className="font-important text-4xl mb-12"
                >
                  {t("definitions.categories_title_var", {
                    name: title,
                  })}
                </h2>
                <TermList terms={childs} />
              </div>
            </section>
          )}
        </>
      )}
    </Layout>
  );
}

export async function getStaticPaths(context) {
  const drupalPaths = await getPathsFromContext(
    ["taxonomy_term--definitions"],
    context
  );

  return {
    paths: filterTaxonomyPathsSlug(drupalPaths, "slug"),
    fallback: "blocking",
  };
}

 async function _getStaticProps(context) {
  const localeContext = {
    locale: context.locale,
    defaultLocale: context.defaultLocale,
  };

  const path = await translatePathFromContext({
    ...context,
    params: {
      slug: ["definition", ...context.params.slug],
    },
  });

  if (!path || !path.jsonapi) {
    return {
      notFound: true,
      revalidate: 30,
    };
  }

  const resource = await getResourceByPath(
    `/definition/${context.params.slug.join("/")}`,
    {
      ...localeContext,
      params: getParams(`${path.entity.type}--${path.entity.bundle}`),
    }
  );

  if (!context.preview && !resource?.status) {
    return {
      notFound: true,
      revalidate: 30,
    };
  }

  switch (resource.type) {
    // For definition taxonomy term, we need :
    // - parents (taxonomy_term--definitions parents of term, recursive until virtual)
    // - nodes (node--page in relation with term)
    // - childs (taxonomy_term--definitions children of term)
    case "taxonomy_term--definitions":
      const nodesParams = new DrupalJsonApiParams();
      const childsParams = new DrupalJsonApiParams();

      return {
        props: {
          globals: await getGlobals(context),
          resource,
          nodes: await getFullResourceCollection(
            "node--page",
            nodesParams
              .addFilter("field_definitions.id", resource.id)
              .addFilter("langcode", context.locale)
              .getQueryObject(),
            localeContext
          ),
          childs: await getResourceCollection("taxonomy_term--definitions", {
            params: childsParams
              .addFilter("parent.id", resource.id)
              .addFilter("langcode", context.locale)
              .getQueryObject(),
            ...localeContext,
          }),
          parents: await getFamilyTree(
            resource.parent[0]?.id,
            "taxonomy_term--definitions",
            localeContext
          ),
        },
        revalidate: 30,
      };
    // For page nodes, we need :
    // - parents (taxonomy_term--definitions parents of term, recursive until virtual)
    case "node--page":
      return {
        props: {
          globals: await getGlobals(context),
          resource,
          parents: await getFamilyTree(
            resource.field_definitions?.id,
            "taxonomy_term--definitions",
            localeContext
          ),
        },
        revalidate: 30,
      };
  }
}


    export async function getStaticProps(ctx) {
        let res = _getStaticProps(ctx)
        if(typeof res.then === 'function') res = await res
        return {
          
          ...res,
          props: {
            ...(res.props || {}),
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/taxonomy_term--definitions/[...slug]',
              loaderName: 'getStaticProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  